import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { auth } from '../firebase.config';
import { createUser } from '../redux/adminSlice';

// Settings tab (round 4): admin invites + static "This portal" facts.
// Replaces the old User Management / Firebase Management / Settings tabs.
// Portal invites are ALWAYS role "admin" now - a root users doc with role
// "user" is a dead end (the portal refuses it as PENDING, the office app
// refuses it as not-a-customer), so there is no role picker here. The
// server IGNORES any password field on createUser (it emails a sign-in
// link instead), so there is no password field either.
const SettingsTab = () => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  // Sync ref guard (owner rule: async buttons disable on first press) -
  // isSending alone can't stop a second click that lands before React
  // re-renders the disabled button.
  const sendingRef = useRef(false);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (sendingRef.current) return;
    sendingRef.current = true;
    setIsSending(true);
    setInviteError(null);
    try {
      await dispatch(createUser({ email: email.trim(), role: 'admin' })).unwrap();
      // createUser's thunk already alert()s on success.
      setEmail('');
    } catch (err) {
      setInviteError(typeof err === 'string' ? err : 'Failed to add user. Please try again.');
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
        <h2 className="mb-1 text-xl font-bold text-[#1d2b33]">Portal administrators</h2>
        <p className="mb-4 text-sm text-[#5b6d7a]">
          Administrators can sign in here and manage every customer. The person you invite gets
          an email link to set a password.
        </p>
        <form onSubmit={handleInvite} className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            placeholder="Email address"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
          />
          <button
            type="submit"
            disabled={isSending}
            className="rounded-lg bg-[#2f66b8] px-4 py-2 font-semibold text-white transition hover:bg-[#285aa5] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSending ? 'Sending…' : 'Invite administrator'}
          </button>
        </form>
        {inviteError && <p className="mt-2 text-sm text-[#d96a5b]">{inviteError}</p>}
      </div>

      <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
        <h2 className="mb-4 text-xl font-bold text-[#1d2b33]">This portal</h2>
        <ul className="space-y-2 text-sm text-[#1d2b33]">
          <li>
            Signed in as <span className="font-semibold">{auth.currentUser?.email}</span>
          </li>
          <li>
            Firebase project <span className="font-semibold">test-aced1</span>
          </li>
          <li>
            <a
              href="https://simplicity-office.web.app"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#3d78c9] underline hover:text-[#2f5a9e]"
            >
              SimpleOffice — simplicity-office.web.app
            </a>
          </li>
          <li>
            <a
              href="https://console.firebase.google.com/project/test-aced1/overview"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#3d78c9] underline hover:text-[#2f5a9e]"
            >
              Firebase console
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SettingsTab;
